import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTheme } from '../context/ThemeContext'
import { apiConfig } from '../auth/authConfig'
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
    ResponsiveContainer, ReferenceLine
} from 'recharts'

function WorkoverDetail() {
    const { theme } = useTheme()
    const { id } = useParams()
    const navigate = useNavigate()
    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [expandedDays, setExpandedDays] = useState({})

    useEffect(() => { fetchData() }, [id])

    const fetchData = async () => {
        setLoading(true)
        try {
            const res = await fetch(`${apiConfig.baseUrl}/workovers/detail/${id}`)
            if (!res.ok) throw new Error('Workover not found')
            const json = await res.json()
            setData(json)
        } catch (err) {
            setError(err.message)
        } finally {
            setLoading(false)
        }
    }

    const toggleDay = (idx) => {
        setExpandedDays(prev => ({ ...prev, [idx]: !prev[idx] }))
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-aviator-600"></div>
            </div>
        )
    }

    if (error || !data?.workover) {
        return (
            <div className="space-y-4">
                <button onClick={() => navigate('/workovers')}
                    className={`text-sm ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`}>
                    &larr; Back to Workovers
                </button>
                <div className="bg-red-900/20 border border-red-800 rounded-lg p-4 text-red-400">
                    <p className="font-semibold">Error: {error || 'Workover not found'}</p>
                </div>
            </div>
        )
    }

    const wo = data.workover
    const production = data.production || []
    const wellHistory = data.wellHistory || []
    const dailyReports = data.dailyReports || []

    const startDateStr = wo.startDate ? new Date(wo.startDate).toISOString().split('T')[0] : null

    return (
        <div className="space-y-6">
            {/* Back nav + header */}
            <div className={`border-b-2 ${theme === 'dark' ? 'border-blue-800' : 'border-blue-200'} pb-4`}>
                <button onClick={() => navigate('/workovers')}
                    className={`text-sm mb-2 inline-block ${theme === 'dark' ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'}`}>
                    &larr; Back to Workovers
                </button>
                <h1 className={`text-xl sm:text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} flex items-center gap-2 sm:gap-3`}>
                    <span className="inline-block w-1 h-6 sm:h-8 bg-blue-500 flex-shrink-0"></span>
                    {wo.woNumber || (wo.source === 'snowflake' ? wo.workType : `WO-${wo.id}`)} &mdash; {wo.well}
                    {wo.source === 'snowflake' && (
                        <span className="ml-2 px-2 py-0.5 text-xs rounded bg-indigo-900 text-indigo-300 font-normal">ProdView</span>
                    )}
                </h1>
                <div className="flex items-center gap-3 mt-2 ml-4">
                    <span className={`inline-flex px-2 py-1 rounded text-xs font-semibold ${
                        wo.status === 'Active'
                            ? theme === 'dark' ? 'bg-green-900 text-green-200' : 'bg-green-100 text-green-800'
                            : wo.status === 'completed'
                            ? theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
                            : theme === 'dark' ? 'bg-yellow-900 text-yellow-200' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                        {wo.status}
                    </span>
                    <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                        {wo.duration} days
                    </span>
                </div>
            </div>

            {/* Detail Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                <DetailCard theme={theme} label="Work Type" value={wo.workType || '--'} />
                <DetailCard theme={theme} label="Reason" value={wo.reason || '--'} />
                <DetailCard theme={theme} label="Rig" value={wo.rig || '--'} />
                <DetailCard theme={theme} label="Deferred BOPD" value={wo.defBopd?.toFixed(1) || '--'} />
                <DetailCard theme={theme} label="Estimated Cost" value={wo.estCost ? `$${wo.estCost.toLocaleString()}` : '--'} />
                <DetailCard theme={theme} label="Actual Cost" value={wo.cost ? `$${wo.cost.toLocaleString()}` : '--'} />
                <DetailCard theme={theme} label="Start Date" value={wo.startDate ? new Date(wo.startDate).toLocaleDateString() : '--'} />
                <DetailCard theme={theme} label="End Date" value={wo.endDate ? new Date(wo.endDate).toLocaleDateString() : 'Ongoing'} />
            </div>

            {/* Notes */}
            {(wo.notes || wo.completionNotes) && (
                <div className={`chart-container`}>
                    <h2 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-3`}>Notes</h2>
                    {wo.notes && (
                        <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-2`}>{wo.notes}</p>
                    )}
                    {wo.completionNotes && (
                        <div className={`mt-2 pt-2 border-t ${theme === 'dark' ? 'border-slate-700' : 'border-gray-200'}`}>
                            <p className={`text-xs font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'} mb-1`}>Completion Notes</p>
                            <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>{wo.completionNotes}</p>
                        </div>
                    )}
                </div>
            )}

            {/* Daily Operations */}
            {dailyReports.length > 0 && (
                <div className="chart-container">
                    <h2 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-4`}>
                        Daily Operations ({dailyReports.length} reports)
                    </h2>
                    <div className="space-y-2">
                        {dailyReports.map((rpt, i) => (
                            <div key={i} className={`rounded-lg border ${theme === 'dark' ? 'border-slate-700 bg-slate-800/50' : 'border-gray-200 bg-gray-50'}`}>
                                <button
                                    onClick={() => toggleDay(i)}
                                    className="w-full px-4 py-3 flex items-center justify-between text-left"
                                >
                                    <div className="flex items-center gap-3 min-w-0">
                                        <span className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                                            theme === 'dark' ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-700'
                                        }`}>
                                            {Math.round(rpt.reportDay)}
                                        </span>
                                        <div className="min-w-0">
                                            <p className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'} truncate`}>
                                                {rpt.activity || 'Operations'}
                                            </p>
                                            <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                                {rpt.reportDate ? new Date(rpt.reportDate + 'T00:00:00').toLocaleDateString() : '--'}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-4 flex-shrink-0">
                                        <div className="text-right hidden sm:block">
                                            <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Day Cost</p>
                                            <p className={`text-sm font-mono font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                                ${(rpt.dayCost || 0).toLocaleString()}
                                            </p>
                                        </div>
                                        <div className="text-right hidden sm:block">
                                            <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Cumulative</p>
                                            <p className={`text-sm font-mono font-medium ${theme === 'dark' ? 'text-emerald-400' : 'text-emerald-600'}`}>
                                                ${(rpt.cumulativeCost || 0).toLocaleString()}
                                            </p>
                                        </div>
                                        <span className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'} transition-transform ${expandedDays[i] ? 'rotate-180' : ''}`}>
                                            &#9660;
                                        </span>
                                    </div>
                                </button>
                                {expandedDays[i] && rpt.summary && (
                                    <div className={`px-4 pb-4 pt-0 border-t ${theme === 'dark' ? 'border-slate-700' : 'border-gray-200'}`}>
                                        <div className="sm:hidden flex gap-4 py-2 mb-2">
                                            <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                                Day: <span className="font-mono font-medium text-white">${(rpt.dayCost || 0).toLocaleString()}</span>
                                            </span>
                                            <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                                Cum: <span className="font-mono font-medium text-emerald-400">${(rpt.cumulativeCost || 0).toLocaleString()}</span>
                                            </span>
                                        </div>
                                        <p className={`text-sm leading-relaxed whitespace-pre-wrap ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                            {rpt.summary}
                                        </p>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Production Chart */}
            {production.length > 0 && (
                <div className="chart-container">
                    <h2 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-4`}>
                        Well Production (Last 90 Days)
                    </h2>
                    <div className="h-80">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={production} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                                <XAxis dataKey="date"
                                    tick={{ fontSize: 12, fill: '#94a3b8' }}
                                    stroke="#475569"
                                    tickFormatter={(d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                />
                                <YAxis yAxisId="left"
                                    tick={{ fontSize: 12, fill: '#94a3b8' }}
                                    stroke="#475569"
                                    label={{ value: 'Oil (BBL)', angle: -90, position: 'insideLeft', style: { fontSize: 12, fill: '#cbd5e1' } }}
                                />
                                <YAxis yAxisId="right" orientation="right"
                                    tick={{ fontSize: 12, fill: '#94a3b8' }}
                                    stroke="#475569"
                                    label={{ value: 'Gas (MCF)', angle: 90, position: 'insideRight', style: { fontSize: 12, fill: '#cbd5e1' } }}
                                />
                                <Tooltip contentStyle={{
                                    backgroundColor: '#1e293b', border: '1px solid #475569',
                                    borderRadius: '8px', color: '#e2e8f0'
                                }} />
                                <Legend wrapperStyle={{ color: '#cbd5e1' }} />
                                {startDateStr && (
                                    <ReferenceLine x={startDateStr} yAxisId="left" stroke="#ef4444" strokeDasharray="5 5"
                                        label={{ value: 'WO Start', position: 'top', fill: '#ef4444', fontSize: 11 }} />
                                )}
                                <Line yAxisId="left" type="monotone" dataKey="oil" stroke="#22c55e" strokeWidth={2} dot={false} name="Oil (BBL)" />
                                <Line yAxisId="right" type="monotone" dataKey="gas" stroke="#ef4444" strokeWidth={2} dot={false} name="Gas (MCF)" />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}

            {/* Well Workover History */}
            {wellHistory.length > 0 && (
                <div className="chart-container">
                    <h2 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-4`}>
                        Workover History for {wo.well}
                    </h2>
                    <div className="overflow-x-auto">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Job Type</th>
                                    <th>Start</th>
                                    <th>End</th>
                                    <th className="text-right">Duration</th>
                                    <th className="text-right">Cost</th>
                                </tr>
                            </thead>
                            <tbody>
                                {wellHistory.map((j, i) => (
                                    <tr key={j.jobId || i}
                                        className={j.jobId ? 'cursor-pointer' : ''}
                                        onClick={() => j.jobId && navigate(`/workovers/detail/sf-${j.jobId}`)}>
                                        <td className="font-medium">
                                            {j.jobType || '--'}
                                            {j.jobId && <span className={`ml-2 text-xs ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`}>&rarr;</span>}
                                        </td>
                                        <td>{j.startDate ? new Date(j.startDate).toLocaleDateString() : '--'}</td>
                                        <td>{j.endDate ? new Date(j.endDate).toLocaleDateString() : '--'}</td>
                                        <td className="text-right">{j.duration != null ? `${j.duration}d` : '--'}</td>
                                        <td className="text-right font-mono">
                                            {j.totalCost ? `$${j.totalCost.toLocaleString()}` : '--'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    )
}

function DetailCard({ theme, label, value }) {
    return (
        <div className={`p-3 sm:p-4 rounded-lg ${theme === 'dark' ? 'bg-slate-900 border border-slate-800' : 'bg-white border border-gray-200'}`}>
            <p className={`text-[10px] sm:text-xs font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{label}</p>
            <p className={`text-sm sm:text-lg font-semibold mt-1 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{value}</p>
        </div>
    )
}

export default WorkoverDetail
